import apiClient from '$utils/apiClient';

const BASE_URL = 'assignments';

const assignments = async ({
  method = 'GET',
  accessToken,
  params,
  url = BASE_URL,
  withCredentials,
}) =>
  apiClient({
    method,
    url,
    headers: { Authorization: `Bearer ${accessToken}` },
    params,
    withCredentials,
  });

export const getAssignmentsBySpaceId = async ({
  spaceId,
  accessToken,
  displayLimit = 1000,
  page = 0,
}) => {
  const { data } = await assignments({
    accessToken,
    params: { spaceId, displayLimit, page },
  });

  return data;
};

export const getAssignmentById = async ({ accessToken, assignmentId }) => {
  const { data } = await assignments({
    url: `${BASE_URL}/${assignmentId}`,
    accessToken,
  });

  return data;
};

// assignmentId can be a list of assignments, separated by ","
// studentId is optional
export const getGradesByAssignmentId = async ({
  spaceId,
  accessToken,
  assignmentId,
  studentId,
}) => {
  const { data } = await apiClient({
    method: 'GET',
    url: `${BASE_URL}/grades/${spaceId}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: {
      assignments: assignmentId,
      ...(studentId && { student: studentId }),
    },
    withCredentials: true,
  });

  return data;
};

export const postAssignment = async ({ accessToken, assignment }) => {
  const { data } = await apiClient({
    method: 'post',
    url: BASE_URL,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: assignment,
    withCredentials: true,
  });

  return data;
};

export const postReleaseGrades = async ({ accessToken, assignmentId }) => {
  const { data } = await apiClient({
    method: 'post',
    url: `${BASE_URL}/${assignmentId}/release-grades`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return data;
};

const postGradeFunction = () => {
  const queue = {};

  return async ({ accessToken, assignmentId, grade, studentId }) => {
    // we only queue multiple requests for the same assignemnt/student
    const queueKey = `${assignmentId}:${studentId}`;
    // If the queue is empty or the prev. request already resolved we'll proceed immediately,
    // otherise wait for the prev request to resolve before proceeding.
    try {
      await queue[queueKey];
    } catch (error) {
      // do nothing as the rejection relates to a prev. grading request
      // and the error would have been handled in the context of that request
    }

    const postGradePromise = apiClient({
      method: 'post',
      url: `${BASE_URL}/${assignmentId}/grade`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      data: { grade, studentId },
      withCredentials: true,
    });

    queue[queueKey] = postGradePromise;

    const { data } = await postGradePromise;
    return data;
  };
};

export const postGrade = postGradeFunction();

export const patchAssignment = async ({
  accessToken,
  updates,
  assignmentId,
}) => {
  const { data } = await apiClient({
    method: 'patch',
    url: `${BASE_URL}/${assignmentId}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: updates,
    withCredentials: true,
  });

  return data;
};

export const deleteAssignment = async ({ accessToken, assignmentId }) => {
  const { data } = await apiClient({
    method: 'delete',
    url: `${BASE_URL}/${assignmentId}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return data;
};

export const postGrades = async ({ accessToken, assignmentId, grades }) => {
  const { data } = await apiClient({
    method: 'post',
    url: `${BASE_URL}/${assignmentId}/import/grades`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: { grades },
    withCredentials: true,
  });

  return data;
};
